import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { catchError, finalize, switchMap, takeUntil, tap } from 'rxjs/operators'

import { takeFormSnapshot } from '@core/helpers/take-form-snapshot'
import { DestroyService } from '@core/services/destroy.service'
import { TrackedUnsavedForm, UnsavedFormTracker } from '@core/services/unsaved-form-tracker.service'
import { ThemeService } from '@shared/services/theme.service'
import { Theme } from '@shared/models/theme.model'
import { ResellerService } from '@app/modules/admin/reseller/services/reseller.service'
import { PlatformSettings } from '@shared/models/platform-settings.model'
import { handleFormErrors } from '@core/helpers/handle-form-errors'
import { PlatformSettingsForm, PlatformSettingsFormData } from './platform-settings-form.model'
import { ApiService } from '@shared/services/api.service'
import { AdminTeamService } from '@app/modules/admin/admin-team/admin-team.service'
import { Currency } from '@shared/models/currency.model'
import { CurrencyService } from '@app/shared/services/currency.service'
import { forkJoin, Observable, of } from 'rxjs'

@Component({
  selector: 'lin-platform-settings-form',
  templateUrl: './platform-settings-form.component.html',
  styleUrls: ['./platform-settings-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  standalone: false,
})
export class PlatformSettingsFormComponent implements OnInit, OnDestroy, TrackedUnsavedForm {
  @Input() id: string
  @Input() user: 'admin' | 'reseller'

  fileDomain: string
  form: FormGroup
  formError: string
  isLoaded: boolean
  logo: string | ArrayBuffer
  favicon: string | ArrayBuffer | SafeUrl
  themes: Theme[]
  currencies: Currency[]
  defaultCurrencyId: number

  isFormChanged: ReturnType<typeof takeFormSnapshot>
  isSubmitting = false

  defaultLogo = 'assets/images/logo-light.svg'
  defaultFavicon = '/assets/favicon_default.ico'

  get faviconUrl(): string | ArrayBuffer | SafeUrl {
    return this.favicon || this.defaultFavicon
  }

  get logoUrl(): string | ArrayBuffer {
    return this.logo || this.defaultLogo
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly unsavedFormTrackerService: UnsavedFormTracker,
    private readonly themeService: ThemeService,
    private readonly cdr: ChangeDetectorRef,
    private readonly resellerService: ResellerService,
    private readonly adminTeamService: AdminTeamService,
    private readonly destroyService: DestroyService,
    private readonly sanitize: DomSanitizer,
    private readonly apiService: ApiService,
    private readonly currencyService: CurrencyService,
  ) {
    this.fileDomain = apiService.fileUrl
  }

  ngOnInit(): void {
    this.unsavedFormTrackerService.track(this)

    // TODO(BE-contract): remove catchError once BE implements `/currencies` (and `/themes` stays stable).
    // See local-docs/reports/admin-api-be-contract.md §8.
    const themes$ = this.themeService.getThemesList().pipe(catchError(() => of([] as Theme[])))
    const currencies$ = this.currencyService.getCurrenciesList().pipe(catchError(() => of([] as Currency[])))

    forkJoin([themes$, currencies$])
      .pipe(
        tap(([themes, currencies]: [Theme[], Currency[]]) => {
          this.themes = themes
          this.currencies = currencies
          this.defaultCurrencyId = currencies.find((currency) => currency.code === 'AUD')?.id
        }),
        tap(() => this.initForm()),
        switchMap(() => this.getPlatformSettings$()),
        finalize(() => (this.isLoaded = true)),
        takeUntil(this.destroyService),
      )
      .subscribe((formdata) => {
        this.setFormValue(formdata)
        this.isFormChanged = takeFormSnapshot(this.form)
        this.cdr.markForCheck()
      })
  }

  initForm(): void {
    this.form = this.formBuilder.group({
      name: ['', Validators.required],
      domain: ['', Validators.required],
      hostApi: [''],
      hostApp: [''],
      hostTrack: [''],
      hostMessenger: [''],
      logo: [''],
      favicon: [''],
      phoneNumber: [
        '',
        // eslint-disable-next-line no-useless-escape
        [Validators.required, Validators.minLength(8), this.patternValidator(/^\+[0-9]+(\,[0-9]+)?$/, { phoneNumber: true })],
      ],
      phoneWorkingHours: [''],
      email: ['', [Validators.required]],
      supportEmail: ['', [Validators.required, Validators.email]],
      accountingEmail: ['', [Validators.email]],
      salesEmail: ['', [Validators.email]],
      sms: ['', Validators.required],
      theme: ['', Validators.required],
      currencyId: ['', Validators.required],
      notificationEmail: ['', [Validators.required, Validators.email]],
      linkAppStore: [''],
      linkPlayMarket: [''],
      linkKnowledgeBase: [''],
      intercomId: [''],
    })

    if (this.user === 'reseller') {
      this.form.addControl('privacyPolicyLink', new FormControl(''))
      this.form.addControl('termsOfUseLink', new FormControl(''))
    }
  }

  uploadImage(file, controlName: string): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (file && file.length) {
      const reader: FileReader = new FileReader()
      const formControl = this.form.get(controlName)

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
      reader.readAsDataURL(file[0])
      reader.onload = (event) => {
        const image = controlName === 'favicon' ? this.sanitize.bypassSecurityTrustUrl(event.target.result as string) : event.target.result
        this[controlName] = image
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
        formControl.setValue(file[0])
        formControl.markAsDirty()
        this.cdr.markForCheck()
      }
    }
  }

  restoreDefaultPicture(pictureTitle: string): void {
    const defaultPicture = {
      logo: this.defaultLogo,
      favicon: this.defaultFavicon,
    }
    const control = this.form.get(pictureTitle)

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this[pictureTitle] = defaultPicture[pictureTitle]
    control.setValue(null)
    control.markAsDirty()
  }

  private setFormValue(formData: PlatformSettings): void {
    if (!('productName' in formData)) return

    this.form.patchValue({
      name: formData.productName,
      domain: formData.domain,
      hostApi: formData.hostApi,
      hostApp: formData.hostApp,
      hostTrack: formData.hostTrack,
      hostMessenger: formData.hostMessenger,
      phoneNumber: formData.supportPhone,
      phoneWorkingHours: formData.supportMsg,
      email: formData.emailName,
      supportEmail: formData.supportEmail,
      accountingEmail: formData.accountingEmail,
      salesEmail: formData.salesEmail,
      sms: formData.smsName,
      theme: formData.clientDefaultTheme?.id || '',
      currencyId: formData.currency?.id || this.defaultCurrencyId,
      notificationEmail: formData.notificationEmail,
      linkPlayMarket: formData.linkPlayMarket,
      linkAppStore: formData.linkAppStore,
      linkKnowledgeBase: formData.linkKnowledgeBase,
      intercomId: formData.intercomId,
    })

    if (this.user === 'reseller') {
      this.form.patchValue({
        privacyPolicyLink: formData.privacyPolicyLink,
        termsOfUseLink: formData.termsOfUseLink,
      })
    }

    this.logo = formData.logo && `${this.fileDomain}${formData.logo?.path}/${formData.logo?.name}`
    this.favicon = formData.favicon && `${this.fileDomain}${formData.favicon?.path}/${formData.favicon?.name}`
    this.isFormChanged = takeFormSnapshot(this.form)
  }

  private getPlatformSettings$(): Observable<PlatformSettings> {
    return this.user === 'admin' ? this.adminTeamService.getPlatformSettings() : this.resellerService.getPlatformSettings(this.id)
  }

  saveData(): void {
    const saveData = this.prepareDataForSave()
    const postUserPlatformSettings$ = {
      admin: this.adminTeamService.updatePlatformSettings(saveData),
      reseller: this.resellerService.updatePlatformSettings(this.id, saveData),
    }

    postUserPlatformSettings$[this.user]
      .pipe(
        switchMap(() => this.getPlatformSettings$()),
        finalize(() => (this.isSubmitting = false)),
        takeUntil(this.destroyService),
      )
      .subscribe((formdata) => {
        this.setFormValue(formdata)
        this.form.markAsPristine()
        this.cdr.markForCheck()
      }, handleFormErrors(this.form))
  }

  private prepareDataForSave(): PlatformSettingsForm {
    const formData = this.form.value as PlatformSettingsFormData
    const { favicon, logo } = this.form.controls

    return {
      productName: formData.name,
      domain: formData.domain,
      hostApi: formData.hostApi,
      hostApp: formData.hostApp,
      hostTrack: formData.hostTrack,
      hostMessenger: formData.hostMessenger,
      ...(logo.dirty && { logo: formData.logo }),
      ...(favicon.dirty && { favicon: formData.favicon }),
      supportPhone: formData.phoneNumber,
      supportMsg: formData.phoneWorkingHours,
      supportEmail: formData.supportEmail,
      accountingEmail: formData.accountingEmail,
      salesEmail: formData.salesEmail,
      emailName: formData.email,
      smsName: formData.sms,
      clientDefaultThemeId: formData.theme,
      currencyId: formData.currencyId,
      notificationEmail: formData.notificationEmail,
      linkPlayMarket: formData.linkPlayMarket,
      linkAppStore: formData.linkAppStore,
      linkKnowledgeBase: formData.linkKnowledgeBase,
      intercomId: formData.intercomId,
      ...(this.user === 'reseller' && { privacyPolicyLink: formData.privacyPolicyLink, termsOfUseLink: formData.termsOfUseLink }),
    }
  }

  private patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return regex.test(control.value) ? null : error
    }
  }

  ngOnDestroy(): void {
    this.unsavedFormTrackerService.discard(this)
  }
}
